import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { userAPI } from '../../lib/api';
import { Layout } from '../../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { toast } from 'sonner';
import { 
    User, 
    Lock, 
    Bell, 
    Shield, 
    FileText,
    Upload,
    Trash2,
    Loader2,
    CheckCircle,
    AlertCircle,
    Camera
} from 'lucide-react';

const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

const ProfilePictureSection = ({ user, onRefresh }) => {
    const fileInputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const handleUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
        if (!allowedTypes.includes(file.type)) {
            toast.error('Please upload a JPEG, PNG, WebP, or GIF image');
            return;
        }
        if (file.size > 2 * 1024 * 1024) {
            toast.error('Image must be less than 2MB');
            return;
        }

        setUploading(true);
        try {
            await userAPI.uploadProfilePicture(file);
            toast.success('Profile picture updated');
            if (onRefresh) onRefresh();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to upload picture');
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('Remove your profile picture?')) return;
        setDeleting(true);
        try {
            await userAPI.deleteProfilePicture();
            toast.success('Profile picture removed');
            if (onRefresh) onRefresh();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to remove picture');
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div className="flex items-center gap-6 pb-6 mb-6 border-b" data-testid="profile-picture-section">
            <div className="relative group">
                <Avatar className="h-20 w-20 ring-2 ring-neutral-200">
                    <AvatarImage src={user?.picture} alt={user?.name} />
                    <AvatarFallback className="bg-primary-100 text-primary-700 text-xl font-semibold">
                        {getInitials(user?.name)}
                    </AvatarFallback>
                </Avatar>
                <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                    data-testid="profile-picture-overlay"
                >
                    {uploading ? (
                        <Loader2 className="w-6 h-6 text-white animate-spin" />
                    ) : (
                        <Camera className="w-6 h-6 text-white" />
                    )}
                </button>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    onChange={handleUpload}
                    className="hidden"
                    data-testid="profile-picture-input"
                />
            </div>
            <div className="flex-1">
                <h3 className="font-medium text-neutral-900">{user?.name || 'Your Name'}</h3>
                <p className="text-sm text-neutral-500">{user?.email}</p>
                <div className="flex gap-2 mt-3">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        data-testid="upload-picture-btn"
                    >
                        {uploading ? (
                            <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Uploading...</>
                        ) : (
                            <><Camera className="w-4 h-4 mr-1" /> {user?.picture ? 'Change Photo' : 'Upload Photo'}</>
                        )}
                    </Button>
                    {user?.picture && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleDelete}
                            disabled={deleting}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid="delete-picture-btn"
                        >
                            {deleting ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <><Trash2 className="w-4 h-4 mr-1" /> Remove</>
                            )}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};

const SettingsPage = () => {
    const { user, refreshUser } = useAuth();
    const [loading, setLoading] = useState(false);
    const [uploadingCV, setUploadingCV] = useState(false);
    const [deletingCV, setDeletingCV] = useState(false);
    
    const [profileForm, setProfileForm] = useState({
        name: '',
        email: '',
        phone: '',
    });

    const [passwordForm, setPasswordForm] = useState({
        current_password: '',
        new_password: '',
        confirm_password: '',
    });

    const [notifications, setNotifications] = useState({
        email_updates: true,
        job_alerts: true,
        marketing: false,
    });

    useEffect(() => {
        if (user) {
            setProfileForm({
                name: user.name || '',
                email: user.email || '',
                phone: user.phone || '',
            });
        }
    }, [user]);

    const handleProfileUpdate = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await userAPI.updateProfile({
                name: profileForm.name,
                phone: profileForm.phone,
            });
            toast.success('Profile updated successfully');
            if (refreshUser) refreshUser();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update profile');
        } finally {
            setLoading(false);
        }
    };

    const handlePasswordChange = async (e) => {
        e.preventDefault();
        
        if (passwordForm.new_password !== passwordForm.confirm_password) {
            toast.error('New passwords do not match');
            return;
        }
        
        if (passwordForm.new_password.length < 8) {
            toast.error('Password must be at least 8 characters');
            return;
        }

        setLoading(true);
        try {
            await userAPI.changePassword({
                current_password: passwordForm.current_password,
                new_password: passwordForm.new_password
            });
            toast.success('Password changed successfully');
            setPasswordForm({
                current_password: '',
                new_password: '',
                confirm_password: '',
            });
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to change password');
        } finally {
            setLoading(false);
        }
    };

    const handleCVUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
        if (!allowedTypes.includes(file.type)) {
            toast.error('Please upload a PDF, DOC, or DOCX file');
            return;
        }

        if (file.size > 10 * 1024 * 1024) {
            toast.error('File size must be less than 10MB');
            return;
        }

        setUploadingCV(true);
        try {
            await userAPI.uploadCV(file);
            toast.success('CV uploaded successfully');
            if (refreshUser) refreshUser();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to upload CV');
        } finally {
            setUploadingCV(false);
        }
    };

    const handleCVDelete = async () => {
        if (!window.confirm('Are you sure you want to delete your CV?')) return;
        
        setDeletingCV(true);
        try {
            await userAPI.deleteCV();
            toast.success('CV deleted successfully');
            if (refreshUser) refreshUser();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to delete CV');
        } finally {
            setDeletingCV(false);
        }
    };

    return (
        <Layout>
            <div className="min-h-screen bg-neutral-50 py-8">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="mb-8">
                        <h1 className="text-3xl font-bold text-neutral-900" data-testid="settings-heading">Settings</h1>
                        <p className="text-neutral-600 mt-2">Manage your account settings and preferences</p>
                    </div>

                    <Tabs defaultValue="profile" className="space-y-6">
                        <TabsList className="grid w-full grid-cols-4">
                            <TabsTrigger value="profile" className="flex items-center gap-2" data-testid="tab-profile">
                                <User className="w-4 h-4" />
                                <span className="hidden sm:inline">Profile</span>
                            </TabsTrigger>
                            <TabsTrigger value="security" className="flex items-center gap-2" data-testid="tab-security">
                                <Lock className="w-4 h-4" />
                                <span className="hidden sm:inline">Security</span>
                            </TabsTrigger>
                            <TabsTrigger value="cv" className="flex items-center gap-2" data-testid="tab-cv">
                                <FileText className="w-4 h-4" />
                                <span className="hidden sm:inline">CV</span>
                            </TabsTrigger>
                            <TabsTrigger value="notifications" className="flex items-center gap-2" data-testid="tab-notifications">
                                <Bell className="w-4 h-4" />
                                <span className="hidden sm:inline">Notifications</span>
                            </TabsTrigger>
                        </TabsList>

                        {/* Profile Tab */}
                        <TabsContent value="profile">
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <User className="w-5 h-5" />
                                        Profile Information
                                    </CardTitle>
                                    <CardDescription>
                                        Update your personal information and profile picture
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <ProfilePictureSection user={user} onRefresh={refreshUser} />

                                    <form onSubmit={handleProfileUpdate} className="space-y-6" data-testid="profile-form">
                                        <div className="grid gap-4 sm:grid-cols-2">
                                            <div className="space-y-2">
                                                <Label htmlFor="name">Full Name</Label>
                                                <Input
                                                    id="name"
                                                    data-testid="input-name"
                                                    value={profileForm.name}
                                                    onChange={(e) => setProfileForm(prev => ({ ...prev, name: e.target.value }))}
                                                    placeholder="Your full name"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label htmlFor="email">Email Address</Label>
                                                <Input
                                                    id="email"
                                                    type="email"
                                                    data-testid="input-email"
                                                    value={profileForm.email}
                                                    disabled
                                                    className="bg-neutral-100"
                                                />
                                                <p className="text-xs text-neutral-500">Email cannot be changed</p>
                                            </div>
                                            <div className="space-y-2">
                                                <Label htmlFor="phone">Phone Number</Label>
                                                <Input
                                                    id="phone"
                                                    data-testid="input-phone"
                                                    value={profileForm.phone}
                                                    onChange={(e) => setProfileForm(prev => ({ ...prev, phone: e.target.value }))}
                                                    placeholder="+44 7XXX XXXXXX"
                                                />
                                            </div>
                                        </div>

                                        {/* Subscription Info */}
                                        <div className="p-4 bg-neutral-50 rounded-lg border">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <p className="font-medium">Current Plan</p>
                                                    <p className="text-sm text-neutral-500 capitalize">
                                                        {user?.subscription_tier || 'Free'} Plan
                                                    </p>
                                                </div>
                                                <Button variant="outline" size="sm" asChild>
                                                    <a href="/pricing">Upgrade Plan</a>
                                                </Button>
                                            </div>
                                        </div>

                                        <div className="flex justify-end">
                                            <Button type="submit" disabled={loading} data-testid="save-profile-btn">
                                                {loading ? (
                                                    <>
                                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                        Saving...
                                                    </>
                                                ) : (
                                                    'Save Changes'
                                                )}
                                            </Button>
                                        </div>
                                    </form>
                                </CardContent>
                            </Card>
                        </TabsContent>

                        {/* Security Tab */}
                        <TabsContent value="security">
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Shield className="w-5 h-5" />
                                        Security Settings
                                    </CardTitle>
                                    <CardDescription>
                                        Manage your password and security preferences
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <form onSubmit={handlePasswordChange} className="space-y-6" data-testid="password-form">
                                        <div className="space-y-4">
                                            <div className="space-y-2">
                                                <Label htmlFor="current_password">Current Password</Label>
                                                <Input
                                                    id="current_password"
                                                    type="password"
                                                    data-testid="input-current-password"
                                                    value={passwordForm.current_password}
                                                    onChange={(e) => setPasswordForm(prev => ({ ...prev, current_password: e.target.value }))}
                                                    placeholder="Enter current password"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label htmlFor="new_password">New Password</Label>
                                                <Input
                                                    id="new_password"
                                                    type="password"
                                                    data-testid="input-new-password"
                                                    value={passwordForm.new_password}
                                                    onChange={(e) => setPasswordForm(prev => ({ ...prev, new_password: e.target.value }))}
                                                    placeholder="Enter new password"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label htmlFor="confirm_password">Confirm New Password</Label>
                                                <Input
                                                    id="confirm_password"
                                                    type="password"
                                                    data-testid="input-confirm-password"
                                                    value={passwordForm.confirm_password}
                                                    onChange={(e) => setPasswordForm(prev => ({ ...prev, confirm_password: e.target.value }))}
                                                    placeholder="Confirm new password"
                                                />
                                            </div>
                                        </div>

                                        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                                            <div className="flex items-start gap-3">
                                                <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                                                <div className="text-sm text-amber-800">
                                                    <p className="font-medium">Password Requirements</p>
                                                    <ul className="list-disc list-inside mt-1 space-y-1">
                                                        <li>At least 8 characters long</li>
                                                        <li>Include uppercase and lowercase letters</li>
                                                        <li>Include at least one number</li>
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex justify-end">
                                            <Button type="submit" disabled={loading} data-testid="change-password-btn">
                                                {loading ? (
                                                    <>
                                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                        Changing...
                                                    </>
                                                ) : (
                                                    'Change Password'
                                                )}
                                            </Button>
                                        </div>
                                    </form>
                                </CardContent>
                            </Card>
                        </TabsContent>

                        {/* CV Tab */}
                        <TabsContent value="cv">
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <FileText className="w-5 h-5" />
                                        CV Management
                                    </CardTitle>
                                    <CardDescription>
                                        Upload and manage your CV for job applications
                                    </CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-6">
                                    {user?.cv_url ? (
                                        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    <CheckCircle className="w-6 h-6 text-emerald-600" />
                                                    <div>
                                                        <p className="font-medium text-emerald-900">CV Uploaded</p>
                                                        <p className="text-sm text-emerald-700">
                                                            {user.cv_filename || 'Your CV is ready for applications'}
                                                        </p>
                                                        {user.cv_uploaded_at && (
                                                            <p className="text-xs text-emerald-600 mt-1">
                                                                Uploaded: {new Date(user.cv_uploaded_at).toLocaleDateString()}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="flex gap-2">
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={handleCVDelete}
                                                        disabled={deletingCV}
                                                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                        data-testid="delete-cv-btn"
                                                    >
                                                        {deletingCV ? (
                                                            <Loader2 className="w-4 h-4 animate-spin" />
                                                        ) : (
                                                            <Trash2 className="w-4 h-4" />
                                                        )}
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="p-4 bg-neutral-50 border-2 border-dashed border-neutral-300 rounded-lg">
                                            <div className="text-center">
                                                <FileText className="w-12 h-12 text-neutral-400 mx-auto mb-4" />
                                                <p className="font-medium text-neutral-900">No CV uploaded</p>
                                                <p className="text-sm text-neutral-500 mt-1">
                                                    Upload your CV to apply for jobs with Multi-Apply
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    <div className="space-y-4">
                                        <div>
                                            <Label htmlFor="cv-upload" className="block mb-2">
                                                {user?.cv_url ? 'Replace CV' : 'Upload CV'}
                                            </Label>
                                            <div className="flex items-center gap-4">
                                                <Input
                                                    id="cv-upload"
                                                    type="file"
                                                    accept=".pdf,.doc,.docx"
                                                    onChange={handleCVUpload}
                                                    disabled={uploadingCV}
                                                    className="max-w-md"
                                                    data-testid="cv-upload-input"
                                                />
                                                {uploadingCV && (
                                                    <div className="flex items-center gap-2 text-primary-600">
                                                        <Loader2 className="w-4 h-4 animate-spin" />
                                                        <span className="text-sm">Uploading...</span>
                                                    </div>
                                                )}
                                            </div>
                                            <p className="text-xs text-neutral-500 mt-2">
                                                Accepted formats: PDF, DOC, DOCX (Max 10MB)
                                            </p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </TabsContent>

                        {/* Notifications Tab */}
                        <TabsContent value="notifications">
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Bell className="w-5 h-5" />
                                        Notification Preferences
                                    </CardTitle>
                                    <CardDescription>
                                        Choose what notifications you want to receive
                                    </CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-6">
                                    <div className="space-y-4">
                                        <div className="flex items-center justify-between p-4 border rounded-lg">
                                            <div>
                                                <p className="font-medium">Email Updates</p>
                                                <p className="text-sm text-neutral-500">
                                                    Receive updates about your applications and account
                                                </p>
                                            </div>
                                            <input
                                                type="checkbox"
                                                checked={notifications.email_updates}
                                                onChange={(e) => setNotifications(prev => ({ ...prev, email_updates: e.target.checked }))}
                                                className="w-5 h-5"
                                            />
                                        </div>

                                        <div className="flex items-center justify-between p-4 border rounded-lg">
                                            <div>
                                                <p className="font-medium">Job Alerts</p>
                                                <p className="text-sm text-neutral-500">
                                                    Get notified when new jobs match your preferences
                                                </p>
                                            </div>
                                            <input
                                                type="checkbox"
                                                checked={notifications.job_alerts}
                                                onChange={(e) => setNotifications(prev => ({ ...prev, job_alerts: e.target.checked }))}
                                                className="w-5 h-5"
                                            />
                                        </div>

                                        <div className="flex items-center justify-between p-4 border rounded-lg">
                                            <div>
                                                <p className="font-medium">Marketing Emails</p>
                                                <p className="text-sm text-neutral-500">
                                                    Receive tips, updates, and promotional content
                                                </p>
                                            </div>
                                            <input
                                                type="checkbox"
                                                checked={notifications.marketing}
                                                onChange={(e) => setNotifications(prev => ({ ...prev, marketing: e.target.checked }))}
                                                className="w-5 h-5"
                                            />
                                        </div>
                                    </div>

                                    <div className="flex justify-end">
                                        <Button onClick={() => toast.success('Notification preferences saved')}>
                                            Save Preferences
                                        </Button>
                                    </div>
                                </CardContent>
                            </Card>
                        </TabsContent>
                    </Tabs>
                </div>
            </div>
        </Layout>
    );
};

export default SettingsPage;
